import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';

class AuthService {
  // User registration
  async register(userData) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, userData);
      
      if (response.success) {
        const { user, accessToken, refreshToken } = response.data;
        
        // Store tokens and user data
        apiClient.setToken(accessToken);
        apiClient.setRefreshToken(refreshToken);
        this.setUser(user);
        
        return { user, success: true };
      }
      
      throw new Error(response.message || 'Registration failed');
    } catch (error) {
      throw new Error(error.message || 'Registration failed');
    }
  }

  // User login
  async login(email, password) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
      });
      
      if (response.success) {
        const { user, accessToken, refreshToken } = response.data;
        
        // Store tokens and user data
        apiClient.setToken(accessToken);
        apiClient.setRefreshToken(refreshToken);
        this.setUser(user);
        
        return { user, success: true };
      }
      
      throw new Error(response.message || 'Login failed');
    } catch (error) {
      throw new Error(error.message || 'Login failed');
    }
  }

  // Google Sign-In (accepts credential or access_token)
  async googleLogin(tokenData) {
    try {
      const payload = typeof tokenData === 'string'
        ? { credential: tokenData }
        : { access_token: tokenData.access_token };
      const response = await apiClient.post(API_ENDPOINTS.AUTH.GOOGLE, payload);

      if (response.success) {
        const { user, accessToken, refreshToken } = response.data;

        apiClient.setToken(accessToken);
        apiClient.setRefreshToken(refreshToken);
        this.setUser(user);

        return { user, success: true };
      }

      throw new Error(response.message || 'Google login failed');
    } catch (error) {
      throw new Error(error.message || 'Google login failed');
    }
  }

  // User logout
  async logout() {
    try {
      const refreshToken = apiClient.getRefreshToken();
      
      // Call logout endpoint to invalidate refresh token on server
      if (refreshToken) {
        await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT, { refreshToken });
      }
    } catch (error) {
      // Continue with logout even if server call fails
    } finally {
      // Always clear local storage
      this.clearUserData();
    }
  }

  // Get current user from server
  async getCurrentUser() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      
      if (response.success) {
        const { user, stats, currentPlan } = response.data;
        
        // Update stored user data with currentPlan
        const fullUserData = { ...user, stats, currentPlan };
        this.setUser(fullUserData);
        
        return fullUserData;
      }
      
      throw new Error(response.message || 'Failed to get user data');
    } catch (error) {
      throw new Error(error.message || 'Failed to get user data');
    }
  }

  // Refresh access token
  async refreshToken() {
    try {
      const refreshToken = apiClient.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.post(API_ENDPOINTS.AUTH.REFRESH, {
        refreshToken,
      });
      
      if (response.success) {
        const { accessToken } = response.data;
        apiClient.setToken(accessToken);
        return true;
      }
      
      throw new Error('Token refresh failed');
    } catch (error) {
      this.clearUserData();
      throw error;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = apiClient.getToken();
    const user = this.getUser();
    
    return !!(token && user);
  }

  // Check if token is expired (basic check)
  isTokenExpired() {
    const token = apiClient.getToken();
    
    if (!token) return true;
    
    try {
      // Basic JWT payload decode (not secure, just for expiry check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  // User data management
  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  getUser() {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  }

  clearUserData() {
    apiClient.clearTokens();
    localStorage.removeItem('user');
  }

  // Password change
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.post('/api/users/change-password', {
        currentPassword,
        newPassword,
      });
      
      if (response.success) {
        // Password changed successfully, user needs to login again
        this.clearUserData();
        return true;
      }
      
      throw new Error(response.message || 'Password change failed');
    } catch (error) {
      throw new Error(error.message || 'Password change failed');
    }
  }

  // Account deactivation
  async deactivateAccount(password) {
    try {
      const response = await apiClient.post('/api/users/deactivate', {
        password,
      });
      
      if (response.success) {
        this.clearUserData();
        return true;
      }
      
      throw new Error(response.message || 'Account deactivation failed');
    } catch (error) {
      throw new Error(error.message || 'Account deactivation failed');
    }
  }

  // Get user sessions
  async getUserSessions() {
    try {
      const response = await apiClient.get('/api/users/sessions');
      
      if (response.success) {
        return response.data.sessions;
      }
      
      throw new Error(response.message || 'Failed to get user sessions');
    } catch (error) {
      throw new Error(error.message || 'Failed to get user sessions');
    }
  }

  // Revoke user session
  async revokeSession(sessionId) {
    try {
      const response = await apiClient.delete(`/api/users/sessions/${sessionId}`);
      
      if (response.success) {
        return true;
      }
      
      throw new Error(response.message || 'Failed to revoke session');
    } catch (error) {
      throw new Error(error.message || 'Failed to revoke session');
    }
  }

  // Initialize auth state on app load
  async initializeAuth() {
    try {
      // Check if we have tokens
      if (!this.isAuthenticated()) {
        return null;
      }

      // Check if token is expired
      if (this.isTokenExpired()) {
        try {
          await this.refreshToken();
        } catch (error) {
          this.clearUserData();
          return null;
        }
      }

      // Get current user data from server
      const user = await this.getCurrentUser();
      return user;
    } catch (error) {
      this.clearUserData();
      return null;
    }
  }

  // Forgot password - Request password reset
  async forgotPassword(email) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        email,
      });

      if (response.success) {
        return response;
      }

      throw new Error(response.message || 'Failed to send password reset email');
    } catch (error) {
      throw new Error(error.message || 'Failed to send password reset email');
    }
  }

  // Reset password with token
  async resetPassword(token, password) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        token,
        password,
      });

      if (response.success) {
        return response;
      }

      throw new Error(response.message || 'Failed to reset password');
    } catch (error) {
      throw new Error(error.message || 'Failed to reset password');
    }
  }

  // Verify email with token
  async verifyEmail(token) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
        token,
      });

      if (response.success) {
        return response;
      }

      throw new Error(response.message || 'Failed to verify email');
    } catch (error) {
      throw new Error(error.message || 'Failed to verify email');
    }
  }

  // Resend verification email
  async resendVerificationEmail(email) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        email,
      });

      if (response.success) {
        return response;
      }

      throw new Error(response.message || 'Failed to resend verification email');
    } catch (error) {
      throw new Error(error.message || 'Failed to resend verification email');
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;